// src/components/resources/quizzes/hooks/useQuizState.ts

import { useState, useCallback } from 'react'
import { FeedbackTiming } from '~/lib/resources/types/tools/quiz'
import { isAnswerCorrect as gradeAnswer } from '~/components/resources/quizzes/utils/answer-correctness'
import type { 
  Quiz, 
  QuizResult, 
  ScoreBreakdown
} from '~/lib/resources/types/tools/quiz'
import type { QuizState } from '~/components/resources/quizzes/types/quiz-types'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useQuizState(quiz: Quiz) {
  const [state, setState] = useState<QuizState>({
    currentSectionIndex: 0,
    currentQuestionIndex: 0,
    answers: {},
    result: null,
    showExplanation: false,
    showHint: false,
    showSettings: false,
    isReviewingSection: false,
    sectionResults: {},
    feedbackPreferences: {
      timing: quiz.config.feedbackDefaults?.timing ?? FeedbackTiming.IMMEDIATE,
      settings: {
        showVisualIndicators: quiz.config.feedbackDefaults?.timing !== FeedbackTiming.SECTION,
        showDetailedExplanations: quiz.config.feedbackDefaults?.settings?.showDetailedExplanations ?? true,
        enableHints: quiz.config.feedbackDefaults?.settings?.enableHints ?? true,
        showSectionSummaries: quiz.config.feedbackDefaults?.timing === FeedbackTiming.SECTION
      }
    },
    history: {
      answers: {},
      sections: quiz.sections.map(section => ({
        id: section.id,
        questions: section.questions.map(q => ({
          id: q.id,
          answered: false,
          correct: false
        }))
      }))
    },
    email: {
      address: '',
      showModal: false,
      isSending: false,
      error: null,
      success: false
    },
    isComplete: false,
    started: false,
    startedAt: null
  })

  const currentSection = quiz.sections[state.currentSectionIndex]
  const currentQuestion = currentSection?.questions[state.currentQuestionIndex]

  const isAnswerCorrect = useCallback((questionId: string): boolean => {
    const question = quiz.sections.flatMap(s => s.questions).find(q => q.id === questionId)
    const answer = state.answers[questionId]
    
    if (!question || !answer) return false

    return gradeAnswer(question, answer)
  }, [quiz.sections, state.answers])

  const handleAnswer = useCallback((questionId: string, answer: string | string[]) => {
    const question = quiz.sections.flatMap(s => s.questions).find(q => q.id === questionId)
    if (!question) return

    setState(prev => {
      // Only show immediate feedback for single-select questions
      // Multiselect and ordered questions require submit button
      const shouldShowFeedback = prev.feedbackPreferences.timing === FeedbackTiming.IMMEDIATE && 
        prev.feedbackPreferences.settings.showDetailedExplanations &&
        question.type !== 'multiselect' && 
        question.type !== 'ordered'

      const isCorrect = gradeAnswer(question, answer)

      return {
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: answer
        },
        history: {
          ...prev.history,
          answers: {
            ...prev.history.answers,
            [questionId]: {
              answer,
              timestamp: new Date().toISOString(),
              isCorrect
            }
          },
          sections: prev.history.sections.map(section => ({
            ...section,
            questions: section.questions.map(q => 
              q.id === questionId
                ? { ...q, answered: true, correct: isCorrect }
                : q
            )
          }))
        },
        showExplanation: !!(shouldShowFeedback && prev.feedbackPreferences.settings.showDetailedExplanations),
        showHint: false
      }
    })
  }, [quiz.sections])

  const submitAnswer = useCallback((questionId: string) => {
    const question = quiz.sections.flatMap(s => s.questions).find(q => q.id === questionId)
    if (!question) return

    setState(prev => {
      // For ordered questions, only show feedback when all items are placed
      if (question.type === 'ordered') {
        const answer = prev.answers[questionId]
        if (!Array.isArray(answer) || answer.length !== question.correctAnswer.length) {
          return prev
        }
      }

      return {
        ...prev,
        showExplanation: !!prev.feedbackPreferences.settings.showDetailedExplanations
      }
    })
  }, [quiz.sections])

  const isSectionComplete = useCallback((sectionIndex: number): boolean => {
    const section = quiz.sections[sectionIndex]
    if (!section) return false

    return section.questions.every(question => {
      const answer = state.answers[question.id]
      if (!answer) return false

      // For multiselect/ordered questions, require submission
      if (question.type === 'multiselect' || question.type === 'ordered') {
        return Array.isArray(answer) && 
          answer.length > 0 && 
          state.showExplanation // Must have been submitted
      }

      // For single select, just need an answer
      return true
    })
  }, [quiz.sections, state.answers, state.showExplanation])

  const shouldShowSectionSummary = useCallback((sectionIndex: number): boolean => {
    return isSectionComplete(sectionIndex) && 
      !state.result && 
      !state.isReviewingSection && 
      state.feedbackPreferences.timing === FeedbackTiming.SECTION
  }, [isSectionComplete, state.result, state.isReviewingSection, state.feedbackPreferences.timing])

  const calculateScore = useCallback((): QuizResult => {
    const totalCorrect = quiz.sections.reduce((sum, section) => {
      return sum + section.questions.reduce((sectionSum, question) => {
        return sectionSum + (isAnswerCorrect(question.id) ? 1 : 0)
      }, 0)
    }, 0)

    const totalQuestions = quiz.sections.reduce(
      (sum, section) => sum + section.questions.length,
      0
    )

    const overallScore = Math.round((totalCorrect / totalQuestions) * 100)
    const passed = overallScore >= (quiz.config.passingScore ?? 70)

    // Calculate achievement level
    const achievement = quiz.config.results?.thresholds?.achievements 
      ? Object.entries(quiz.config.results.thresholds.achievements)
          .sort(([, a], [, b]) => b - a)
          .find(([, threshold]) => overallScore >= threshold)?.[0] as 'expert' | 'advanced' | 'intermediate' | 'beginner'
      : undefined

    // Calculate section results
    const sectionResults = Object.fromEntries(
      quiz.sections.map(section => {
        const questions = section.questions
        const correctAnswers = questions.reduce((count, question) => 
          count + (isAnswerCorrect(question.id) ? 1 : 0), 0)
        const total = questions.length
        const percentage = Math.round((correctAnswers / total) * 100)

        // Find matching section trigger
        const trigger = quiz.config.results?.sectionTriggers?.[section.id]?.find(t => 
          percentage >= t.minScore && (!t.maxScore || percentage <= t.maxScore)
        )

        return [section.id, {
          score: correctAnswers,
          total,
          percentage,
          feedback: trigger ? {
            message: trigger.feedback,
            strengths: trigger.strengths,
            areasForImprovement: trigger.areasForImprovement,
            recommendations: trigger.recommendations,
            nextSteps: trigger.nextSteps
          } : undefined,
          achievement
        }]
      })
    )

    // Calculate category results
    const categoryResults: Record<string, ScoreBreakdown> = {}
    const categoryQuestions = new Map<string, { correct: number; total: number }>()

    quiz.sections.forEach(section => {
      section.questions.forEach(question => {
        if (question.category) {
          const stats = categoryQuestions.get(question.category) ?? { correct: 0, total: 0 }
          stats.total++
          if (isAnswerCorrect(question.id)) stats.correct++
          categoryQuestions.set(question.category, stats)
        }
      })
    })

    categoryQuestions.forEach((stats, category) => {
      const percentage = Math.round((stats.correct / stats.total) * 100)
      
      // Find matching category trigger
      const trigger = quiz.config.results?.categoryTriggers?.[category]?.find(t =>
        percentage >= t.minScore && (!t.maxScore || percentage <= t.maxScore)
      )

      categoryResults[category] = {
        score: stats.correct,
        total: stats.total,
        percentage,
        feedback: trigger ? {
          message: trigger.feedback,
          strengths: trigger.strengths,
          areasForImprovement: trigger.areasForImprovement,
          recommendations: trigger.recommendations,
          nextSteps: trigger.nextSteps
        } : undefined,
        achievement
      }
    })

    // Calculate platform results if questions have platform tags
    const platformResults: Record<string, ScoreBreakdown> = {}
    const platformQuestions = new Map<string, { correct: number; total: number }>()

    quiz.sections.forEach(section => {
      section.questions.forEach(question => {
        if (question.platform) {
          const stats = platformQuestions.get(question.platform) ?? { correct: 0, total: 0 }
          stats.total++
          if (isAnswerCorrect(question.id)) stats.correct++
          platformQuestions.set(question.platform, stats)
        }
      })
    })

    platformQuestions.forEach((stats, platform) => {
      const percentage = Math.round((stats.correct / stats.total) * 100)
      
      // Find matching platform trigger
      const trigger = quiz.config.results?.platformTriggers?.[platform]?.find(t =>
        percentage >= t.minScore && (!t.maxScore || percentage <= t.maxScore)
      )

      platformResults[platform] = {
        score: stats.correct,
        total: stats.total,
        percentage,
        feedback: trigger ? {
          message: trigger.feedback,
          strengths: trigger.strengths,
          areasForImprovement: trigger.areasForImprovement,
          recommendations: trigger.recommendations,
          nextSteps: trigger.nextSteps
        } : undefined,
        achievement
      }
    })

    // Find overall trigger based on score
    const overallTrigger = quiz.config.results?.overallTriggers
      .filter(t => overallScore >= t.minScore && (!t.maxScore || overallScore <= t.maxScore))
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))[0]

    // Track all question responses
    const responses = quiz.sections.flatMap(section =>
      section.questions.map(question => {
        const answerTimestamp = state.history.answers[question.id]?.timestamp
        // Elapsed time is measured from the stored quiz-start timestamp. The
        // old code checked `typeof state.started === 'string'`, but `started`
        // is a boolean, so startTime was always 0 and `timeTaken` was an
        // absolute epoch-ms value rather than a duration.
        const startTime = state.startedAt ? new Date(state.startedAt).getTime() : 0
        const timeTaken = answerTimestamp && startTime
          ? new Date(answerTimestamp).getTime() - startTime
          : undefined

        return {
          questionId: question.id,
          sectionId: section.id,
          category: question.category,
          platform: question.platform,
          correct: isAnswerCorrect(question.id),
          answer: state.answers[question.id] ?? '',
          timeTaken
        }
      })
    )

    return {
      score: overallScore,
      totalQuestions,
      passed,
      achievement,
      sectionResults,
      categoryResults,
      platformResults,
      responses,
      feedback: {
        summary: overallTrigger?.feedback ?? `You scored ${overallScore}% on this quiz.`,
        strengths: overallTrigger?.strengths ?? [],
        areasForImprovement: overallTrigger?.areasForImprovement ?? [],
        recommendations: overallTrigger?.recommendations ?? [],
        nextSteps: overallTrigger?.nextSteps ?? []
      }
    }
    // `state.started` is intentionally NOT a dependency: the elapsed-time
    // calculation reads `state.startedAt` (the stored quiz-start timestamp), not
    // the boolean `state.started`. It was a leftover dep from the removed
    // `typeof state.started === 'string'` path (see comment above), so including
    // it only forced needless recomputation when the flag toggled.
  }, [quiz.sections, quiz.config.results, quiz.config.passingScore, isAnswerCorrect, state.answers, state.history.answers, state.startedAt])

  const toggleHint = useCallback(() => {
    setState(prev => ({
      ...prev,
      showHint: !prev.showHint
    }))
  }, [])

  const setFeedbackPreferences = useCallback((preferences: QuizState['feedbackPreferences']) => {
    setState(prev => ({
      ...prev,
      feedbackPreferences: preferences
    }))
  }, [])

  const startSectionReview = useCallback(() => {
    setState(prev => ({
      ...prev,
      isReviewingSection: true
    }))
  }, [])

  const endSectionReview = useCallback(() => {
    setState(prev => ({
      ...prev,
      isReviewingSection: false
    }))
  }, [])

  const resetQuiz = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentSectionIndex: 0,
      currentQuestionIndex: 0,
      answers: {},
      result: null,
      showExplanation: false,
      showHint: false,
      showSettings: false,
      isReviewingSection: false,
      sectionResults: {},
      history: {
        answers: {},
        sections: quiz.sections.map(section => ({
          id: section.id,
          questions: section.questions.map(q => ({
            id: q.id,
            answered: false,
            correct: false
          }))
        }))
      },
      isComplete: false,
      started: false,
      startedAt: null
    }))
  }, [quiz.sections])

  return {
    state,
    setState,
    handleAnswer,
    toggleHint,
    isAnswerCorrect,
    calculateScore,
    submitAnswer,
    setFeedbackPreferences,
    startSectionReview,
    endSectionReview,
    shouldShowSectionSummary,
    currentSection,
    currentQuestion,
    resetQuiz
  }
} 